import { computed, defineComponent, nextTick, onMounted, ref } from 'vue'
import {
  deleteArticle,
  getArticleDetail,
  getArticleList,
  getResourceList,
  getUploadTempSignature,
  saveArticle
} from './api'

const ARTICLE_STATUS_OPTIONS = [
  { value: 'draft', label: '草稿' },
  { value: 'pending', label: '待审核' },
  { value: 'published', label: '已发布' },
  { value: 'rejected', label: '已拒绝' },
  { value: 'archived', label: '已归档' }
]

const SOURCE_TYPE_OPTIONS = [
  { value: 'original', label: '原创' },
  { value: 'genealogy', label: '族谱' },
  { value: 'local_history', label: '地方志' },
  { value: 'interview', label: '访谈' },
  { value: 'reprint', label: '转载' }
]

const BLOCK_TYPE_OPTIONS = [
  { value: 'paragraph', label: '段落' },
  { value: 'subheading', label: '小标题' },
  { value: 'image', label: '图片' },
  { value: 'audio', label: '音频' },
  { value: 'video', label: '视频' },
  { value: 'gallery', label: '图集' },
  { value: 'timeline', label: '时间线' },
  { value: 'quote', label: '引用' }
]

const EMPTY_ARTICLE = {
  id: '',
  category_id: '',
  title: '',
  subtitle: '',
  summary: '',
  cover_url: '',
  author: '',
  source_name: '',
  source_type: 'original',
  surname: '',
  dynasty: '',
  period_text: '',
  region_code: '',
  region_name: '',
  status: 'draft',
  is_featured: false,
  is_recommended: false
}

function createBlock(type = 'paragraph') {
  const id = Date.now() + Math.random()
  switch (type) {
    case 'image':
      return { id, type, url: '', caption: '' }
    case 'audio':
      return { id, type, url: '', title: '', duration: '', cover: '' }
    case 'video':
      return { id, type, url: '', poster: '', title: '' }
    case 'gallery':
      return {
        id,
        type,
        title: '',
        items: [{ url: '', caption: '' }]
      }
    case 'timeline':
      return {
        id,
        type,
        title: '',
        items: [{ date: '', title: '', description: '' }]
      }
    case 'quote':
      return { id, type, text: '', source: '' }
    case 'subheading':
      return { id, type: 'subheading', text: '' }
    case 'paragraph':
    default:
      return { id, type: 'paragraph', text: '' }
  }
}

function parseJsonValue(value, fallback) {
  if (value == null || value === '') return fallback
  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch (e) {
      return fallback
    }
  }
  return value
}

function normalizeBlocks(contentJson) {
  const content = parseJsonValue(contentJson, { version: 1, blocks: [] })
  const blocks = content && Array.isArray(content.blocks) ? content.blocks : []
  return blocks.map((block) => {
    const next = { ...createBlock(block.type), ...block }
    next.id = Date.now() + Math.random()
    if (next.type === 'gallery') {
      next.items = Array.isArray(next.items) && next.items.length
        ? next.items.map((item) => ({ url: item.url || item.image_url || '', caption: item.caption || '' }))
        : [{ url: '', caption: '' }]
    }
    if (next.type === 'timeline') {
      next.items = Array.isArray(next.items) && next.items.length
        ? next.items.map((item) => ({
            date: item.date || '',
            title: item.title || '',
            description: item.description || ''
          }))
        : [{ date: '', title: '', description: '' }]
    }
    return next
  })
}

function trimString(value) {
  return String(value == null ? '' : value).trim()
}

function cleanBlock(block) {
  switch (block.type) {
    case 'image':
      return {
        type: 'image',
        url: trimString(block.url),
        caption: trimString(block.caption)
      }
    case 'audio': {
      const duration = Number(block.duration)
      const next = {
        type: 'audio',
        url: trimString(block.url),
        title: trimString(block.title),
        cover: trimString(block.cover)
      }
      if (Number.isFinite(duration) && duration > 0) next.duration = duration
      return next
    }
    case 'video':
      return {
        type: 'video',
        url: trimString(block.url),
        poster: trimString(block.poster),
        title: trimString(block.title)
      }
    case 'gallery':
      return {
        type: 'gallery',
        title: trimString(block.title),
        items: (block.items || [])
          .map((item) => ({
            url: trimString(item.url),
            caption: trimString(item.caption)
          }))
          .filter((item) => item.url || item.caption)
      }
    case 'timeline':
      return {
        type: 'timeline',
        title: trimString(block.title),
        items: (block.items || [])
          .map((item) => ({
            date: trimString(item.date),
            title: trimString(item.title),
            description: trimString(item.description)
          }))
          .filter((item) => item.date || item.title || item.description)
      }
    case 'quote':
      return {
        type: 'quote',
        text: trimString(block.text),
        source: trimString(block.source)
      }
    case 'subheading':
      return {
        type: 'subheading',
        text: trimString(block.text)
      }
    case 'paragraph':
    default:
      return {
        type: 'paragraph',
        text: trimString(block.text)
      }
  }
}

function blockHasContent(block) {
  if (block.type === 'gallery' || block.type === 'timeline') {
    return !!(trimString(block.title) || (block.items && block.items.length > 0))
  }
  if (block.type === 'subheading' || block.type === 'paragraph' || block.type === 'quote') {
    return !!trimString(block.text)
  }
  if (block.type === 'image') {
    return !!(trimString(block.url) || trimString(block.caption))
  }
  if (block.type === 'audio' || block.type === 'video') {
    return !!trimString(block.url)
  }
  return Object.keys(block).some((key) => {
    if (key === 'type' || key === 'id') return false
    const value = block[key]
    if (value == null) return false
    if (typeof value === 'object') return false
    return trimString(value)
  })
}

function unwrapPayload(res) {
  return res && res.data !== undefined ? res.data : res
}

function extractList(res) {
  const payload = unwrapPayload(res)
  if (Array.isArray(payload)) return payload
  if (payload && Array.isArray(payload.data)) return payload.data
  if (payload && Array.isArray(payload.list)) return payload.list
  if (payload && Array.isArray(payload.items)) return payload.items
  if (payload && Array.isArray(payload.records)) return payload.records
  if (payload && Array.isArray(payload.results)) return payload.results
  return []
}

function extractMeta(res) {
  const payload = unwrapPayload(res)
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { total: 0, page: 1, page_size: 10, total_pages: 1 }
  }
  const total = Number(payload.total || payload.count || 0)
  const page = Number(payload.page || payload.current_page || 1)
  const pageSize = Number(payload.page_size || payload.pageSize || 10)
  const totalPages = Number(
    payload.total_pages ||
      payload.totalPages ||
      (total && pageSize ? Math.ceil(total / pageSize) : 1)
  )
  return {
    total: Number.isFinite(total) ? total : 0,
    page: Number.isFinite(page) ? page : 1,
    page_size: Number.isFinite(pageSize) ? pageSize : 10,
    total_pages: Number.isFinite(totalPages) ? totalPages : 1
  }
}

function getId(row) {
  if (!row || typeof row !== 'object') return ''
  const id = row.id ?? row.article_id ?? row.articleId
  return id == null ? '' : String(id)
}

function getTitle(row) {
  if (!row || typeof row !== 'object') return '—'
  const title = row.title ?? row.name
  return title == null || String(title).trim() === '' ? '—' : String(title)
}

function getCategoryName(row) {
  if (!row || typeof row !== 'object') return '—'
  const name =
    row.category_name ??
    row.categoryName ??
    (row.category && (row.category.name || row.category.title))
  return name == null || String(name).trim() === '' ? '—' : String(name)
}

function getStatusLabel(status) {
  const matched = ARTICLE_STATUS_OPTIONS.find((item) => item.value === status)
  return matched ? matched.label : status || '—'
}

function getSourceTypeLabel(type) {
  const matched = SOURCE_TYPE_OPTIONS.find((item) => item.value === type)
  return matched ? matched.label : type || '—'
}

function getBlockTypeLabel(type) {
  const matched = BLOCK_TYPE_OPTIONS.find((item) => item.value === type)
  return matched ? matched.label : type || '内容块'
}

function formatBool(value) {
  return value === true || value === 1 ? '是' : '否'
}

function formatDate(value) {
  if (!value) return '—'
  return String(value).replace('T', ' ').slice(0, 19)
}

function normalizeDetail(detail) {
  const payload = unwrapPayload(detail)
  if (payload && payload.article && typeof payload.article === 'object') {
    return payload.article
  }
  if (payload && payload.detail && typeof payload.detail === 'object') {
    return payload.detail
  }
  return payload && typeof payload === 'object' ? payload : {}
}

function buildFormFromArticle(article) {
  const form = { ...EMPTY_ARTICLE, ...article }
  form.id = form.id == null ? '' : form.id
  form.category_id = form.category_id == null ? '' : form.category_id
  form.is_featured = form.is_featured === true || form.is_featured === 1
  form.is_recommended = form.is_recommended === true || form.is_recommended === 1
  return form
}

function normalizePersonLinks(value) {
  const list = parseJsonValue(value, [])
  if (!Array.isArray(list)) return []
  return list.map((item) => ({
    person_id: item.person_id == null ? '' : item.person_id,
    relation_type: item.relation_type || 'main'
  }))
}

function normalizeMediaList(value) {
  const list = parseJsonValue(value, [])
  if (!Array.isArray(list)) return []
  return list.map((item, index) => ({
    media_type: item.media_type || 'image',
    url: item.url || item.image_url || '',
    title: item.title || item.caption || '',
    sort_order: item.sort_order == null ? index + 1 : item.sort_order
  }))
}

function normalizeGalleryList(value) {
  const list = parseJsonValue(value, [])
  if (!Array.isArray(list)) return []
  return list.map((gallery) => ({
    title: gallery.title || '',
    cover_url: gallery.cover_url || '',
    items:
      Array.isArray(gallery.items) && gallery.items.length
        ? gallery.items.map((item) => ({
            image_url: item.image_url || item.url || '',
            caption: item.caption || ''
          }))
        : [{ image_url: '', caption: '' }]
  }))
}

function buildPersonLinks(list) {
  return (list || [])
    .map((item) => ({
      person_id: Number(item.person_id),
      relation_type: trimString(item.relation_type) || 'main'
    }))
    .filter((item) => Number.isFinite(item.person_id))
}

function buildMediaList(list) {
  return (list || [])
    .map((item, index) => {
      const sortOrder = Number(item.sort_order)
      return {
        media_type: item.media_type || 'image',
        url: trimString(item.url),
        title: trimString(item.title),
        sort_order: Number.isFinite(sortOrder) ? sortOrder : index + 1
      }
    })
    .filter((item) => item.url || item.title)
}

function buildGalleryList(list) {
  return (list || [])
    .map((gallery) => ({
      title: trimString(gallery.title),
      cover_url: trimString(gallery.cover_url),
      items: (gallery.items || [])
        .map((item) => ({
          image_url: trimString(item.image_url),
          caption: trimString(item.caption)
        }))
        .filter((item) => item.image_url || item.caption)
    }))
    .filter((gallery) => gallery.title || gallery.cover_url || gallery.items.length)
}

export default defineComponent({
  name: 'HeritageView',
  setup() {
    const articleList = ref([])
    const categoryList = ref([])
    const tagList = ref([])
    const personList = ref([])
    const loading = ref(false)
    const saving = ref(false)
    const detailLoading = ref(false)
    const errorMsg = ref('')

    const currentPage = ref(1)
    const pageSize = ref(10)
    const totalPages = ref(1)
    const total = ref(0)
    const statusFilter = ref('')
    const categoryFilter = ref('')
    const keyword = ref('')

    const modalVisible = ref(false)
    const modalTitle = ref('新增文章')
    const articleForm = ref({ ...EMPTY_ARTICLE })
    const selectedTagIds = ref([])
    const contentBlocks = ref([])
    const personLinks = ref([])
    const mediaList = ref([])
    const galleryList = ref([])
    const fileInputRef = ref(null)
    const uploadingAsset = ref(false)
    const uploadTarget = ref(null)
    const uploadAccept = ref('*/*')

    const categoryOptions = computed(() =>
      categoryList.value.map((item) => ({
        id: item.id ?? item.category_id,
        name: item.name ?? item.title ?? item.category_name ?? `分类 ${item.id}`
      }))
    )

    const tagOptions = computed(() =>
      tagList.value.map((item) => ({
        id: item.id ?? item.tag_id,
        name: item.name ?? item.title ?? item.tag_name ?? `标签 ${item.id}`
      }))
    )

    const personOptions = computed(() =>
      personList.value.map((item) => ({
        id: item.id ?? item.person_id,
        name: item.name ?? item.real_name ?? item.title ?? `人物 ${item.id}`
      }))
    )

    const buildParams = () => {
      const params = {
        page: currentPage.value,
        page_size: pageSize.value
      }
      if (statusFilter.value) params.status = statusFilter.value
      if (categoryFilter.value) params.category_id = categoryFilter.value
      const q = String(keyword.value || '').trim()
      if (q) params.q = q
      return params
    }

    const fetchArticles = async () => {
      loading.value = true
      errorMsg.value = ''
      try {
        const res = await getArticleList(buildParams())
        articleList.value = extractList(res)
        const meta = extractMeta(res)
        currentPage.value = meta.page
        pageSize.value = meta.page_size
        totalPages.value = Math.max(1, meta.total_pages)
        total.value = meta.total
      } catch (e) {
        console.error(e)
        articleList.value = []
        errorMsg.value = e.message || '获取文章列表失败，请稍后重试'
      } finally {
        loading.value = false
      }
    }

    const fetchResourceOptions = async () => {
      try {
        const [categories, tags, persons] = await Promise.all([
          getResourceList('category', { page: 1, page_size: 200, status: 'enabled' }),
          getResourceList('tag', { page: 1, page_size: 200 }),
          getResourceList('person', { page: 1, page_size: 200 })
        ])
        categoryList.value = extractList(categories)
        tagList.value = extractList(tags)
        personList.value = extractList(persons)
      } catch (e) {
        console.warn('获取文章资源选项失败:', e)
      }
    }

    const handlePageChange = (page) => {
      currentPage.value = page
      fetchArticles()
    }

    let searchTimer = null
    const handleSearchInput = () => {
      if (searchTimer) clearTimeout(searchTimer)
      searchTimer = setTimeout(() => {
        currentPage.value = 1
        fetchArticles()
      }, 300)
    }

    const handleSearch = () => {
      if (searchTimer) {
        clearTimeout(searchTimer)
        searchTimer = null
      }
      currentPage.value = 1
      fetchArticles()
    }

    const resetFilters = () => {
      statusFilter.value = ''
      categoryFilter.value = ''
      keyword.value = ''
      currentPage.value = 1
      fetchArticles()
    }

    const fillModal = (article) => {
      articleForm.value = buildFormFromArticle(article)
      const tagIds = article.tag_ids || article.tags || []
      selectedTagIds.value = Array.isArray(tagIds)
        ? tagIds
            .map((item) => (typeof item === 'object' ? item.id || item.tag_id : item))
            .filter((item) => item != null)
            .map((item) => Number(item))
            .filter((item) => Number.isFinite(item))
        : []
      contentBlocks.value = normalizeBlocks(article.content_json)
      personLinks.value = normalizePersonLinks(article.person_links)
      mediaList.value = normalizeMediaList(article.media)
      galleryList.value = normalizeGalleryList(article.galleries)
    }

    const openCreate = () => {
      modalTitle.value = '新增文章'
      fillModal({ ...EMPTY_ARTICLE })
      modalVisible.value = true
    }

    const openEdit = async (row) => {
      const id = getId(row)
      if (!id) {
        alert('缺少文章 ID')
        return
      }
      modalTitle.value = `编辑文章 #${id}`
      modalVisible.value = true
      detailLoading.value = true
      try {
        const res = await getArticleDetail(id)
        fillModal(normalizeDetail(res))
      } catch (e) {
        console.error(e)
        alert(e.message || '获取文章详情失败')
        modalVisible.value = false
      } finally {
        detailLoading.value = false
      }
    }

    const closeModal = () => {
      if (saving.value) return
      modalVisible.value = false
    }

    const extractPresignedUrl = (signatureResponse) => {
      const source = signatureResponse?.data || signatureResponse || {}
      const presignedUrl = source.url
      if (!presignedUrl) {
        throw new Error('上传凭证缺少 url 字段，请检查接口返回')
      }
      return presignedUrl
    }

    const uploadToCos = async (file) => {
      const signatureResponse = await getUploadTempSignature(file.name)
      const presignedUrl = extractPresignedUrl(signatureResponse)
      const uploadResponse = await fetch(presignedUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type || 'application/octet-stream'
        },
        body: file
      })
      if (!uploadResponse.ok) {
        throw new Error(`上传 COS 失败: ${uploadResponse.status}`)
      }
      return presignedUrl.split('?')[0]
    }

    const triggerAssetUpload = (target, accept = '*/*') => {
      if (uploadingAsset.value) return
      uploadTarget.value = target
      uploadAccept.value = accept
      if (fileInputRef.value) {
        fileInputRef.value.value = ''
        fileInputRef.value.click()
      }
    }

    const setUploadedUrl = (target, url) => {
      if (!target) return
      if (target.type === 'articleField') {
        articleForm.value[target.field] = url
        return
      }
      if (target.type === 'blockField') {
        const block = contentBlocks.value[target.blockIndex]
        if (block) block[target.field] = url
        return
      }
      if (target.type === 'blockItemField') {
        const block = contentBlocks.value[target.blockIndex]
        const item = block && block.items ? block.items[target.itemIndex] : null
        if (item) item[target.field] = url
        return
      }
      if (target.type === 'mediaField') {
        const item = mediaList.value[target.index]
        if (item) item[target.field] = url
        return
      }
      if (target.type === 'galleryField') {
        const gallery = galleryList.value[target.galleryIndex]
        if (gallery) gallery[target.field] = url
        return
      }
      if (target.type === 'galleryItemField') {
        const gallery = galleryList.value[target.galleryIndex]
        const item = gallery && gallery.items ? gallery.items[target.itemIndex] : null
        if (item) item[target.field] = url
      }
    }

    const handleSelectAsset = async (event) => {
      const file = event.target.files && event.target.files[0]
      if (!file || !uploadTarget.value) return

      uploadingAsset.value = true
      try {
        const url = await uploadToCos(file)
        setUploadedUrl(uploadTarget.value, url)
        alert('上传成功')
      } catch (e) {
        console.error('上传资源失败:', e)
        alert(e.message || '上传失败，请稍后重试')
      } finally {
        uploadingAsset.value = false
        uploadTarget.value = null
      }
    }

    const addContentBlock = (type) => {
      const block = createBlock(type)
      contentBlocks.value = contentBlocks.value.concat(block)
      nextTick(() => {
        const el = document.querySelector(`[data-block-id="${block.id}"]`)
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      })
    }

    const removeContentBlock = (index) => {
      contentBlocks.value.splice(index, 1)
    }

    const moveContentBlock = (index, offset) => {
      const targetIndex = index + offset
      if (targetIndex < 0 || targetIndex >= contentBlocks.value.length) return
      const item = contentBlocks.value.splice(index, 1)[0]
      contentBlocks.value.splice(targetIndex, 0, item)
    }

    const addBlockItem = (block) => {
      if (block.type === 'gallery') {
        block.items.push({ url: '', caption: '' })
      }
      if (block.type === 'timeline') {
        block.items.push({ date: '', title: '', description: '' })
      }
    }

    const removeBlockItem = (block, index) => {
      block.items.splice(index, 1)
      if (!block.items.length) addBlockItem(block)
    }

    const addPersonLink = () => {
      personLinks.value.push({ person_id: '', relation_type: 'main' })
    }

    const removePersonLink = (index) => {
      personLinks.value.splice(index, 1)
    }

    const addMediaItem = () => {
      mediaList.value.push({
        media_type: 'image',
        url: '',
        title: '',
        sort_order: mediaList.value.length + 1
      })
    }

    const removeMediaItem = (index) => {
      mediaList.value.splice(index, 1)
    }

    const addGallery = () => {
      galleryList.value.push({
        title: '',
        cover_url: '',
        items: [{ image_url: '', caption: '' }]
      })
    }

    const removeGallery = (index) => {
      galleryList.value.splice(index, 1)
    }

    const addGalleryItem = (gallery) => {
      gallery.items.push({ image_url: '', caption: '' })
    }

    const removeGalleryItem = (gallery, index) => {
      gallery.items.splice(index, 1)
      if (!gallery.items.length) addGalleryItem(gallery)
    }

    const buildPayload = () => {
      const form = articleForm.value
      if (!String(form.title || '').trim()) throw new Error('请填写文章标题')
      if (!form.category_id) throw new Error('请选择分类')

      const blocks = contentBlocks.value.map(cleanBlock).filter(blockHasContent)

      const payload = {
        ...form,
        category_id: Number(form.category_id),
        is_featured: Boolean(form.is_featured),
        is_recommended: Boolean(form.is_recommended),
        content_json: {
          version: 1,
          blocks
        },
        tag_ids: selectedTagIds.value.map((item) => Number(item)).filter((item) => Number.isFinite(item)),
        person_links: buildPersonLinks(personLinks.value),
        media: buildMediaList(mediaList.value),
        galleries: buildGalleryList(galleryList.value)
      }

      if (!payload.id) delete payload.id
      return payload
    }

    const handleSave = async () => {
      let payload
      try {
        payload = buildPayload()
      } catch (e) {
        alert(e.message)
        return
      }

      saving.value = true
      try {
        await saveArticle(payload)
        alert('保存成功')
        modalVisible.value = false
        fetchArticles()
      } catch (e) {
        console.error(e)
        alert(e.message || '保存文章失败，请稍后重试')
      } finally {
        saving.value = false
      }
    }

    const handleDelete = async (row) => {
      const id = getId(row)
      if (!id) {
        alert('缺少文章 ID')
        return
      }
      if (!confirm(`确定要删除文章“${getTitle(row)}”吗？`)) return

      loading.value = true
      try {
        await deleteArticle(Number(id))
        alert('删除成功')
        fetchArticles()
      } catch (e) {
        console.error(e)
        alert(e.message || '删除文章失败，请稍后重试')
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      fetchResourceOptions()
      fetchArticles()
    })

    return {
      ARTICLE_STATUS_OPTIONS,
      SOURCE_TYPE_OPTIONS,
      BLOCK_TYPE_OPTIONS,
      articleList,
      categoryOptions,
      tagOptions,
      personOptions,
      loading,
      saving,
      detailLoading,
      errorMsg,
      currentPage,
      pageSize,
      totalPages,
      total,
      statusFilter,
      categoryFilter,
      keyword,
      modalVisible,
      modalTitle,
      articleForm,
      selectedTagIds,
      contentBlocks,
      personLinks,
      mediaList,
      galleryList,
      fileInputRef,
      uploadingAsset,
      uploadAccept,
      getId,
      getTitle,
      getCategoryName,
      getStatusLabel,
      getSourceTypeLabel,
      getBlockTypeLabel,
      formatBool,
      formatDate,
      fetchArticles,
      handlePageChange,
      handleSearchInput,
      handleSearch,
      resetFilters,
      openCreate,
      openEdit,
      closeModal,
      addContentBlock,
      removeContentBlock,
      moveContentBlock,
      addBlockItem,
      removeBlockItem,
      addPersonLink,
      removePersonLink,
      addMediaItem,
      removeMediaItem,
      addGallery,
      removeGallery,
      addGalleryItem,
      removeGalleryItem,
      triggerAssetUpload,
      handleSelectAsset,
      handleSave,
      handleDelete
    }
  }
})
